import { useEffect, useState } from "react";

export const AUTOPLAY_DURATION_MS = 210000;
export const AUTOPLAY_IDLE_DELAY_MS = 5000;

const ACTIVITY_EVENTS = ["pointermove", "pointerdown", "wheel", "touchstart", "keydown"];

export const useIdleAutoplay = () => {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    let idleTimer = 0;
    let animationFrame = 0;
    let lastFrame = 0;

    const stop = () => {
      const wasPlaying = animationFrame !== 0;
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      lastFrame = 0;
      if (wasPlaying) setIsPlaying(false);
    };

    const step = (now) => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (document.hidden || window.scrollY >= maxScroll - 1) {
        stop();
        return;
      }

      animationFrame = requestAnimationFrame(step);
      const elapsed = Math.min(now - lastFrame, 100);
      lastFrame = now;
      const pixelsPerMs = maxScroll / AUTOPLAY_DURATION_MS;
      const nextPosition = Math.min(maxScroll, window.scrollY + pixelsPerMs * elapsed);
      if (window.__lenis) window.__lenis.scrollTo(nextPosition, { immediate: true, force: true });
      else window.scrollTo({ top: nextPosition });
    };

    const start = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (document.hidden || reducedMotion.matches || window.scrollY >= maxScroll - 1) return;
      setIsPlaying(true);
      lastFrame = performance.now();
      animationFrame = requestAnimationFrame(step);
    };

    const schedule = () => {
      window.clearTimeout(idleTimer);
      if (!document.hidden && !reducedMotion.matches) idleTimer = window.setTimeout(start, AUTOPLAY_IDLE_DELAY_MS);
    };

    const onActivity = () => {
      stop();
      schedule();
    };

    const onVisibilityChange = () => {
      stop();
      if (!document.hidden) schedule();
    };

    const onMotionPreferenceChange = () => {
      stop();
      if (!reducedMotion.matches) schedule();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (reducedMotion.addEventListener) reducedMotion.addEventListener("change", onMotionPreferenceChange);
    else reducedMotion.addListener(onMotionPreferenceChange);
    schedule();

    return () => {
      window.clearTimeout(idleTimer);
      cancelAnimationFrame(animationFrame);
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, onActivity));
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener("change", onMotionPreferenceChange);
      else reducedMotion.removeListener(onMotionPreferenceChange);
    };
  }, []);

  return isPlaying;
};